/* eslint-disable react-hooks/exhaustive-deps -- effects intentionally run on mount / on the listed deps; adding the flagged functions risks render loops. */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid
} from '@mui/material';
import {
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  Memory as MemoryIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Checkroom as CheckroomIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  CameraAlt as CameraAltIcon,
  AutoAwesome as AutoAwesomeIcon,
  GridView as GridViewIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { conversationsAPI, calendarAPI, uploadAPI, outfitsAPI } from '../services/api';
import { useUI } from '../contexts/UIContext';
import {
  Conversation,
  ConversationMessage,
  AssistantResponse,
  ConversationContext,
  SearchResultOutfit,
  ProposedOutfit,
} from '../types';

interface ConversationInterfaceProps {
  conversationId: string;
  currentUserId: string;
  onConversationUpdate?: (conversation: Conversation) => void;
}

// Outfit visualization ("Visualize") generates a catalog-style image of a
// recommended outfit via the /upload/visualize-outfit endpoint (Bedrock Stability
// Stable Image Ultra). It is offered, never auto-run, except when the user
// explicitly asks ("show me this outfit") — see visualize_requested below. The
// image is an AI rendering of the outfit, not on-body try-on (Nova Canvas, the
// only Bedrock try-on model, is a blocked legacy model). Set false to hide it.
const VISUALIZE_ENABLED = true;

const ConversationInterface: React.FC<ConversationInterfaceProps> = ({
  conversationId,
  currentUserId,
  onConversationUpdate
}) => {
  const { toast } = useUI();
  const [visualizingId, setVisualizingId] = useState<number | null>(null);
  // Keyed by `${messageId}:${lookIndex}` so each proposed-look card tracks its
  // own Visualize spinner independently of the saved-outfit path above.
  const [visualizingLook, setVisualizingLook] = useState<string | null>(null);
  const [outfitImage, setOutfitImage] = useState<string | null>(null);
  // How the image was produced: 'composite' = a flat-lay of the user's real item
  // photos; 'ai' = a text-to-image impression. Drives the dialog caption.
  const [outfitImageMethod, setOutfitImageMethod] = useState<string | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null);
  const [previewOutfit, setPreviewOutfit] = useState<any | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMessage, setSelectedMessage] = useState<ConversationMessage | null>(null);
  const [showContext, setShowContext] = useState(false);
  const [conversationContext, setConversationContext] = useState<ConversationContext | null>(null);

  // Store outfit recommendations by message ID
  const [messageOutfits, setMessageOutfits] = useState<Record<string, SearchResultOutfit[]>>({});
  // Item-composed looks the assistant proposed, by message ID (the common path —
  // the chat builds outfits from items, not saved Outfit records).
  const [messageProposedOutfits, setMessageProposedOutfits] = useState<Record<string, ProposedOutfit[]>>({});
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadConversation();
  }, [conversationId, currentUserId]); // loadConversation is stable

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversation = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await conversationsAPI.getConversation(
        conversationId,
        { include_memory: false, message_limit: 100 }
      );

      setConversation(response.data);
      setMessages(response.data.messages || []);

      if (onConversationUpdate) {
        onConversationUpdate(response.data);
      }

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const loadConversationContext = async () => {
    try {
      const response = await conversationsAPI.getConversationMemory(
        conversationId,
        { include_global: true }
      );

      // This is a simplified version - the backend would provide full context
      setConversationContext({
        conversation_memories: response.data.memories.filter((m: any) => !m.is_global),
        global_memories: response.data.memories.filter((m: any) => m.is_global),
        recent_messages: messages.slice(-5),
        conversation_summary: conversation?.description || '',
        user_profile: {}
      });
    } catch (err) {
      console.error('Failed to load conversation context:', err);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistically render the user's message immediately (don't wait for the AI reply)
    const tempId = `temp-${Date.now()}`;
    const optimisticUserMessage: ConversationMessage = {
      id: tempId,
      conversation_id: conversationId,
      role: 'user',
      content: messageContent,
      content_type: 'text',
      tokens_used: 0,
      is_edited: false,
      is_deleted: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, optimisticUserMessage]);

    try {
      const response = await conversationsAPI.sendMessage(conversationId, {
        message: messageContent,
        include_search: true
      });

      const assistantResponse: AssistantResponse = response.data;

      // Add assistant response
      const assistantMessage: ConversationMessage = {
        id: assistantResponse.assistant_message_id,
        conversation_id: conversationId,
        role: 'assistant',
        content: assistantResponse.response,
        content_type: 'text',
        tokens_used: assistantResponse.tokens_used,
        processing_time_ms: assistantResponse.processing_time_ms,
        referenced_items: assistantResponse.search_results?.items.map(i => i.id),
        referenced_outfits: assistantResponse.search_results?.outfits.map(o => o.id),
        is_edited: false,
        is_deleted: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Reconcile the optimistic user message with its real id, then append the reply
      setMessages(prev => [
        ...prev.map(m => (m.id === tempId ? { ...m, id: assistantResponse.user_message_id } : m)),
        assistantMessage
      ]);

      // Store saved-outfit recommendations if available
      const recommendedOutfits = assistantResponse.search_results?.outfits || [];
      if (recommendedOutfits.length > 0) {
        setMessageOutfits(prev => ({
          ...prev,
          [assistantMessage.id]: recommendedOutfits
        }));
      }

      // Store item-composed looks the assistant proposed (the usual case)
      const proposedOutfits = assistantResponse.proposed_outfits || [];
      if (proposedOutfits.length > 0) {
        setMessageProposedOutfits(prev => ({
          ...prev,
          [assistantMessage.id]: proposedOutfits
        }));
      }

      // If the user explicitly asked to see an image of the outfit, generate one
      // for the top look right away. We never auto-generate otherwise — the
      // "Visualize" button is the on-demand path for everything else. Prefer the
      // item-composed look the assistant just proposed; fall back to a saved
      // outfit if that's what surfaced instead.
      if (VISUALIZE_ENABLED && assistantResponse.visualize_requested) {
        if (proposedOutfits.length > 0) {
          toast('Generating an image of the top look…', 'info');
          handleVisualizeItems(proposedOutfits[0].item_ids, `${assistantMessage.id}:0`);
        } else if (recommendedOutfits.length > 0) {
          toast('Generating an image of the top look…', 'info');
          handleVisualizeOutfit(recommendedOutfits[0].id);
        }
      }

      // Update conversation stats
      if (conversation) {
        setConversation({
          ...conversation,
          message_count: conversation.message_count + 2,
          total_tokens_used: conversation.total_tokens_used + assistantResponse.tokens_used,
          last_message_at: new Date().toISOString()
        });
      }

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send message');
      setNewMessage(messageContent); // Restore message on error
      setMessages(prev => prev.filter(m => m.id !== tempId)); // drop the optimistic message
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMessageAction = (action: string, message: ConversationMessage) => {
    setSelectedMessage(message);
    setAnchorEl(null);

    switch (action) {
      case 'copy':
        navigator.clipboard.writeText(message.content);
        break;
      case 'edit':
        if (message.role === 'user') {
          setNewMessage(message.content);
        }
        break;
      case 'delete':
        handleDeleteMessage(message.id);
        break;
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await conversationsAPI.deleteMessage(conversationId, messageId);
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? { ...msg, is_deleted: true, content: '[Message deleted]' }
          : msg
      ));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete message');
    }
  };

  const handleViewOutfit = (outfitId: number) => {
    // Navigate to outfit detail page
    window.location.href = `/outfits/${outfitId}`;
  };

  const handlePreviewOutfit = async (outfitId: number) => {
    setPreviewLoadingId(outfitId);
    try {
      const res = await outfitsAPI.getOutfit(outfitId);
      setPreviewOutfit(res.data);
    } catch (err: any) {
      toast(err.response?.data?.error || 'Could not load the outfit', 'error');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const handleVisualizeOutfit = async (outfitId: number) => {
    setVisualizingId(outfitId);
    try {
      const res = await uploadAPI.visualizeOutfit({ outfit_id: outfitId });
      setOutfitImage(res.data.image_url);
      setOutfitImageMethod(res.data.method || null);
    } catch (err: any) {
      toast(err.response?.data?.error || 'Could not generate the outfit image.', 'error');
    } finally {
      setVisualizingId(null);
    }
  };

  // Visualize an item-composed look the assistant proposed (no saved Outfit
  // record, so we send its item_ids). `key` scopes the spinner to one card.
  const handleVisualizeItems = async (itemIds: number[], key: string) => {
    setVisualizingLook(key);
    try {
      const res = await uploadAPI.visualizeOutfit({ item_ids: itemIds });
      setOutfitImage(res.data.image_url);
      setOutfitImageMethod(res.data.method || null);
    } catch (err: any) {
      toast(err.response?.data?.error || 'Could not generate the outfit image.', 'error');
    } finally {
      setVisualizingLook(null);
    }
  };

  const handleSetTodaysFit = async (outfitId: number, outfitName?: string) => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      await calendarAPI.setDailyOutfit({ date: today, default_outfit_id: outfitId });
      toast(`Set "${outfitName || 'outfit'}" as today's fit — confirm it on the Fit of the Day page`, 'success', { duration: 6000 });
    } catch (err: any) {
      toast(err.response?.data?.error || "Couldn't set today's fit", 'error');
    }
  };

  const renderOutfitRecommendations = (messageId: string) => {
    const outfits = messageOutfits[messageId];
    if (!outfits || outfits.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckroomIcon fontSize="small" color="primary" />
          Outfit Recommendations ({outfits.length})
        </Typography>
        <Grid container spacing={2}>
          {outfits.map((outfit) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={outfit.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {outfit.thumbnail_url && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={outfit.thumbnail_url}
                    alt={outfit.name}
                    sx={{ objectFit: 'cover' }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
                      {outfit.name}
                    </Typography>
                    {outfit.is_favorite && (
                      <StarIcon fontSize="small" color="warning" />
                    )}
                  </Box>

                  {outfit.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {outfit.description}
                    </Typography>
                  )}

                  <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                    {outfit.occasion && (
                      <Chip label={outfit.occasion} size="small" variant="outlined" />
                    )}
                    {outfit.season && (
                      <Chip label={outfit.season} size="small" variant="outlined" />
                    )}
                    {outfit.style && (
                      <Chip label={outfit.style} size="small" variant="outlined" />
                    )}
                  </Box>

                  {outfit.items && outfit.items.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {outfit.items.length} items
                    </Typography>
                  )}

                  {outfit.similarity_score && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Match: {(outfit.similarity_score * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                  )}

                  {outfit.match_reasons && outfit.match_reasons.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {outfit.match_reasons.join(', ')}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={previewLoadingId === outfit.id ? <CircularProgress size={14} /> : <GridViewIcon />}
                    disabled={previewLoadingId !== null}
                    onClick={() => handlePreviewOutfit(outfit.id)}
                  >
                    {previewLoadingId === outfit.id ? 'Loading…' : 'Preview'}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewOutfit(outfit.id)}
                  >
                    Details
                  </Button>
                  <Button
                    size="small"
                    startIcon={<CameraAltIcon />}
                    onClick={() => handleSetTodaysFit(outfit.id, outfit.name)}
                  >
                    Today's Fit
                  </Button>
                  {VISUALIZE_ENABLED && (
                    <Button
                      size="small"
                      startIcon={visualizingId === outfit.id ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                      disabled={visualizingId !== null}
                      onClick={() => handleVisualizeOutfit(outfit.id)}
                    >
                      {visualizingId === outfit.id ? 'Generating…' : 'Visualize'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Item-composed looks the assistant proposed inline (see backend [[OUTFIT]]
  // blocks). These are the usual case in chat — outfits built from individual
  // items rather than saved Outfit records — and each gets a Visualize button so
  // the assistant's "tap Visualize" offer is always backed by a real control.
  const renderProposedOutfits = (messageId: string) => {
    const looks = messageProposedOutfits[messageId];
    if (!looks || looks.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckroomIcon fontSize="small" color="primary" />
          Suggested {looks.length === 1 ? 'look' : 'looks'}
        </Typography>
        <Grid container spacing={2}>
          {looks.map((look, idx) => {
            const key = `${messageId}:${idx}`;
            return (
              <Grid size={{ xs: 12, sm: 6 }} key={key}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: 1 }}>
                      {look.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {look.items.map((item) => (
                        <Box key={item.id} sx={{ width: 72, textAlign: 'center' }}>
                          {item.thumbnail_url ? (
                            <Box
                              component="img"
                              src={item.thumbnail_url}
                              alt={item.name}
                              sx={{ width: 72, height: 72, objectFit: 'cover', objectPosition: 'center top', borderRadius: 1, bgcolor: '#f5f5f5' }}
                            />
                          ) : (
                            <Box sx={{ width: 72, height: 72, borderRadius: 1, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <CheckroomIcon color="disabled" fontSize="small" />
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.5 }}>
                            {item.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                  {VISUALIZE_ENABLED && (
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={visualizingLook === key ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                        disabled={visualizingLook !== null}
                        onClick={() => handleVisualizeItems(look.item_ids, key)}
                      >
                        {visualizingLook === key ? 'Generating…' : 'Visualize'}
                      </Button>
                    </CardActions>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const renderMessage = (message: ConversationMessage, index: number) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';
    
    if (message.is_deleted && message.content === '[Message deleted]') {
      return (
        <ListItem key={message.id} sx={{ py: 1, opacity: 0.5 }}>
          <Typography variant="body2" color="textSecondary" fontStyle="italic">
            Message deleted
          </Typography>
        </ListItem>
      );
    }

    return (
      <ListItem
        key={message.id}
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          py: 1,
          px: 2
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            mx: 1,
            bgcolor: isUser ? 'primary.main' : isSystem ? 'grey.500' : 'secondary.main'
          }}
        >
          {isUser ? 'U' : isSystem ? 'S' : 'AI'}
        </Avatar>

        <Box
          sx={{
            maxWidth: '70%',
            bgcolor: isUser ? 'primary.light' : isSystem ? 'grey.100' : 'grey.50',
            borderRadius: 2,
            p: 2,
            position: 'relative'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              color: isUser ? 'primary.contrastText' : 'text.primary'
            }}
          >
            {message.content}
          </Typography>

          {/* Message metadata */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
              opacity: 0.7
            }}
          >
            <Typography variant="caption">
              {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
            </Typography>

            <Box display="flex" alignItems="center" gap={0.5}>
              {message.tokens_used > 0 && (
                <Chip
                  label={`${message.tokens_used} tokens`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.6rem' }}
                />
              )}

              {message.processing_time_ms && (
                <Chip
                  label={`${message.processing_time_ms}ms`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.6rem' }}
                />
              )}

              {message.is_edited && (
                <Chip
                  label="edited"
                  size="small"
                  variant="outlined"
                  sx={{ height: 16, fontSize: '0.6rem' }}
                />
              )}

              <IconButton
                size="small"
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setSelectedMessage(message);
                }}
                sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Referenced items */}
          {message.referenced_items && message.referenced_items.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color="textSecondary">
                Referenced {message.referenced_items.length} items
              </Typography>
            </Box>
          )}
        </Box>

        {/* Outfit Recommendations - shown outside the message box for better visibility */}
        {message.role === 'assistant' && renderOutfitRecommendations(message.id)}
        {message.role === 'assistant' && renderProposedOutfits(message.id)}
      </ListItem>
    );
  };

  const renderContextDialog = () => (
    <Dialog open={showContext} onClose={() => setShowContext(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        Conversation Context & Memory
        <IconButton
          onClick={loadConversationContext}
          sx={{ float: 'right' }}
        >
          <RefreshIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {conversationContext ? (
          <Box>
            <Typography variant="h6" gutterBottom>Global Memories</Typography>
            {conversationContext.global_memories.length > 0 ? (
              <List dense>
                {conversationContext.global_memories.map((memory) => (
                  <ListItem key={memory.id}>
                    <ListItemText
                      primary={memory.key.replace(/_/g, ' ')}
                      secondary={`${memory.value} (confidence: ${(memory.confidence_score * 100).toFixed(0)}%)`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary">No global memories</Typography>
            )}

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Conversation Memories</Typography>
            {conversationContext.conversation_memories.length > 0 ? (
              <List dense>
                {conversationContext.conversation_memories.map((memory) => (
                  <ListItem key={memory.id}>
                    <ListItemText
                      primary={memory.key.replace(/_/g, ' ')}
                      secondary={`${memory.value} (confidence: ${(memory.confidence * 100).toFixed(0)}%)`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography color="textSecondary">No conversation memories</Typography>
            )}
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowContext(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <CircularProgress />
        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
          Loading conversation...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!conversation) {
    return (
      <Alert severity="error">
        Conversation not found. It may have been deleted or you may not have access to it.
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Conversation Header */}
      <Paper sx={{ p: 2, mb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{conversation.title}</Typography>
            {conversation.description && (
              <Typography variant="body2" color="textSecondary">
                {conversation.description}
              </Typography>
            )}
          </Box>
          
          <Box display="flex" gap={1}>
            <Tooltip title="View Memory & Context">
              <IconButton onClick={() => setShowContext(true)}>
                <MemoryIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={conversation.is_pinned ? "Unpin" : "Pin"}>
              <IconButton>
                {conversation.is_pinned ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Conversation Stats */}
        <Box display="flex" gap={2} mt={1}>
          <Chip
            label={`${conversation.message_count} messages`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${conversation.total_tokens_used} tokens used`}
            size="small"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Messages */}
      <Paper sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List sx={{ pb: 0 }}>
            {messages.map((message, index) => renderMessage(message, index))}
          </List>
          <div ref={messagesEndRef} />
        </Box>

        {sending && (
          <Box sx={{ p: 1 }}>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
              AI is thinking...
            </Typography>
          </Box>
        )}

        {/* Message Input */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box display="flex" gap={1} alignItems="flex-end">
            <TextField
              ref={messageInputRef}
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask about your wardrobe, get styling advice, or plan outfits..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sending}
              variant="outlined"
              size="small"
            />
            
            <IconButton
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              color="primary"
              sx={{ mb: 0.5 }}
            >
              {sending ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Message Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleMessageAction('copy', selectedMessage!)}>
          <CopyIcon sx={{ mr: 1 }} fontSize="small" />
          Copy
        </MenuItem>
        {selectedMessage?.role === 'user' && (
          <MenuItem onClick={() => handleMessageAction('edit', selectedMessage!)}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        )}
        <MenuItem onClick={() => handleMessageAction('delete', selectedMessage!)}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {renderContextDialog()}

      <Dialog open={!!previewOutfit} onClose={() => setPreviewOutfit(null)} maxWidth="md" fullWidth>
        <DialogTitle>{previewOutfit?.name || 'Outfit'}</DialogTitle>
        <DialogContent>
          {previewOutfit?.description && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {previewOutfit.description}
            </Typography>
          )}
          <Grid container spacing={2}>
            {(previewOutfit?.items || []).map((oi: any) => {
              const item = oi.item || oi;
              return (
                <Grid size={{ xs: 6, sm: 4 }} key={oi.id || item.id}>
                  <Card variant="outlined">
                    {item.image_url ? (
                      <CardMedia
                        component="img"
                        image={item.image_url}
                        alt={item.name}
                        sx={{ height: 160, objectFit: 'cover', objectPosition: 'center top', bgcolor: '#f5f5f5' }}
                      />
                    ) : (
                      <Box sx={{ height: 160, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckroomIcon color="disabled" />
                      </Box>
                    )}
                    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {item.subcategory || item.category}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {(!previewOutfit?.items || previewOutfit.items.length === 0) && (
            <Typography variant="body2" color="textSecondary">This outfit has no items yet.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleViewOutfit(previewOutfit.id)}>Open full outfit</Button>
          <Button onClick={() => setPreviewOutfit(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!outfitImage} onClose={() => setOutfitImage(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Outfit visualization</DialogTitle>
        <DialogContent>
          {outfitImage && (
            <Box
              component="img"
              src={outfitImage}
              alt="Visualization of the outfit"
              sx={{ width: '100%', borderRadius: 1 }}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {outfitImageMethod === 'stylized'
              ? 'Styled by AI from your actual item photos (Bedrock).'
              : outfitImageMethod === 'composite'
              ? 'A flat-lay composed from your actual item photos.'
              : 'An AI-generated impression of the outfit — not a photo of your exact items.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutfitImage(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationInterface;